import { Box, Grid, Stack, SvgIcon, Typography } from '@mui/material';
import {
  AppFormikTextField,
  AppPrimaryButton,
  AppSecondaryButton,
} from 'shared/components/AppComponents';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { Formik, FormikHelpers } from 'formik';
import { SensitivityScheme } from 'validators/SensitivityScheme';
import { SensitivityService } from 'services/SensitivityService';
import { Toast, Utils } from 'shared/helpers';
import { BROADCAST_MESSAGES } from 'shared/Constants';
import { ISensitivityType } from './BiometricDataStatus';
import { useEffect, useState } from 'react';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';

interface InputCompanySensitivyTypeProps {
  editData?: ISensitivityType | null;
  onEditComplete?: () => void;
}

const InputCompanySensitivyType: React.FC<InputCompanySensitivyTypeProps> = ({
  editData,
  onEditComplete,
}) => {
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const isEditMode = !!editData;
  const DefaulatForm = {
    name: '',
    description: '',
  };
  const [formData, setFormData] = useState({
    ...DefaulatForm,
  });

  // UI Actions
  const actionOnSubmit = (
    formValues: any,
    formik: FormikHelpers<any>
  ): void => {
    const submitPromise = isEditMode
      ? SensitivityService.update(editData._id, formValues)
      : SensitivityService.create(formValues);

    submitPromise
      .then(() => {
        Toast.showSuccessMessage(
          isEditMode
            ? 'Sensitivity type updated successfully.'
            : 'Sensitivity type added successfully.'
        );
        formik.resetForm();

        if (onEditComplete) {
          onEditComplete();
        }
        window.postMessage(BROADCAST_MESSAGES.RELOAD_SENSITIVITY);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        formik.setSubmitting(false);
      });
  };

  useEffect(() => {
    if (editData) {
      setFormData(editData);
    } else {
      setFormData({ ...DefaulatForm });
    }
  }, [editData]);

  // UI Elements
  return (
    <Box>
      <Stack direction="column">
        <Typography variant="h6">
          {isEditMode
            ? 'Edit company sensitivity type'
            : 'Add new company sensitivity type'}
        </Typography>
        <Formik
          enableReinitialize
          initialValues={formData}
          validateOnMount={false}
          validationSchema={SensitivityScheme}
          onSubmit={(formValues, formik) => {
            actionOnSubmit(formValues, formik);
          }}
        >
          {({ isSubmitting, isValid, handleSubmit, resetForm }) => (
            <Grid container className="sensitivity-input">
              <Grid item sm={4} xs={12}>
                <Stack direction="column">
                  <AppFormikTextField
                    outerLabel={false}
                    name="name"
                    placeholder="Name"
                  />
                  {/* <Typography component="span">0/80 characters</Typography> */}
                </Stack>
              </Grid>
              <Grid item sm={4} xs={12}>
                <Stack direction="column">
                  <AppFormikTextField
                    outerLabel={false}
                    name="description"
                    placeholder="Description (optional)"
                  />
                  {/* <Typography component="span">0/80 characters</Typography> */}
                </Stack>
              </Grid>
              <Grid item sm={4} xs={12}>
                {orgACLConfig.aiInventory?.includes(
                  'aiInventory.sensitivityTypes.addNewSensitivity'
                ) && (
                  <AppPrimaryButton
                    isLoading={isSubmitting}
                    disabled={!isValid || isSubmitting}
                    // title="Add sensitivity Type"
                    sx={{ whiteSpace: 'nowrap' }}
                    title={
                      editData
                        ? 'Update sensitivity Type'
                        : 'Add sensitivity Type'
                    }
                    startIcon={
                      <>
                        {!editData && (
                          <SvgIcon
                            component={Plus}
                            inheritViewBox
                            className="view-icon"
                            fontSize="inherit"
                            htmlColor="#76A81F"
                          />
                        )}
                      </>
                    }
                    onClick={handleSubmit}
                  />
                )}
                <Box className="mt-2">
                  {editData && (
                    <AppSecondaryButton
                      title="Cancel"
                      onClick={() => {
                        resetForm();
                        if (onEditComplete) {
                          onEditComplete();
                        }
                      }}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </Formik>
      </Stack>
    </Box>
  );
};

export default InputCompanySensitivyType;
